import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import './TimeDatePickerModal.css';

const TimeDatePickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  mode = 'datetime', // 'date', 'time', 'datetime', 'daterange', 'timerange', 'datetimerange'
  initialDate = null,
  initialTime = null,
  initialEndDate = null,
  initialEndTime = null,
  minDate = null,
  maxDate = null,
  title = null
}) => {
  const { t, isRTL } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(initialDate || '');
  const [selectedTime, setSelectedTime] = useState(initialTime || '09:00');
  const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate || '');
  const [selectedEndTime, setSelectedEndTime] = useState(initialEndTime || '17:00');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'time'
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedDate(initialDate || '');
      setSelectedTime(initialTime || '09:00');
      setSelectedEndDate(initialEndDate || '');
      setSelectedEndTime(initialEndTime || '17:00');
      if (initialDate) {
        setCurrentMonth(new Date(initialDate));
      }
    }
  }, [isOpen, initialDate, initialTime, initialEndDate, initialEndTime]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const dateStr = formatDate(date);
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    const dateStr = formatDate(date);
    if (mode.includes('range')) {
      return dateStr === selectedDate || dateStr === selectedEndDate;
    }
    return dateStr === selectedDate;
  };

  const isDateInRange = (date) => {
    if (!date || !mode.includes('range')) return false;
    const dateStr = formatDate(date);
    if (!selectedDate || !selectedEndDate) return false;
    return dateStr >= selectedDate && dateStr <= selectedEndDate;
  };

  const handleDateClick = (date, event) => {
    if (!date || isDateDisabled(date)) return;
    const dateStr = formatDate(date);

    if (mode.includes('range')) {
      if (!selectedDate || (selectedDate && selectedEndDate)) {
        // Start new range
        setSelectedDate(dateStr);
        setSelectedEndDate('');
      } else if (selectedDate && !selectedEndDate) {
        // Complete range
        if (dateStr >= selectedDate) {
          setSelectedEndDate(dateStr);
        } else {
          setSelectedEndDate(selectedDate);
          setSelectedDate(dateStr);
        }
      }
    } else {
      setSelectedDate(dateStr);
    }

    // Blur the button after selection to remove focus state on mobile
    if (event && event.target) {
      event.target.blur();
    }
  };

  const parseTime = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return { hours: hours || 0, minutes: minutes || 0 };
  };

  const formatTime = (hours, minutes) => {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleHourChange = (hours, isEnd = false) => {
    const current = isEnd ? selectedEndTime : selectedTime;
    const { minutes } = parseTime(current);
    const newTime = formatTime(hours, minutes);
    if (isEnd) {
      setSelectedEndTime(newTime);
    } else {
      setSelectedTime(newTime);
    }
  };

  const handleMinuteChange = (minutes, isEnd = false) => {
    const current = isEnd ? selectedEndTime : selectedTime;
    const { hours } = parseTime(current);
    const newTime = formatTime(hours, minutes);
    if (isEnd) {
      setSelectedEndTime(newTime);
    } else {
      setSelectedTime(newTime);
    }
  };

  const handleConfirm = () => {
    if (mode.includes('date') && !selectedDate) {
      alert(t('dashboard.timeDatePicker.selectDate') || 'Please select a date');
      return;
    }
    if (mode.includes('range') && mode.includes('date') && !selectedEndDate) {
      alert(t('dashboard.timeDatePicker.selectEndDate') || 'Please select an end date');
      return;
    }

    const result = {
      date: selectedDate,
      time: mode.includes('time') ? selectedTime : null,
      endDate: mode.includes('range') ? selectedEndDate : null,
      endTime: mode.includes('range') && mode.includes('time') ? selectedEndTime : null
    };

    onConfirm(result);
    onClose();
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = [
    t('dashboard.timeDatePicker.monthJan') || 'January',
    t('dashboard.timeDatePicker.monthFeb') || 'February',
    t('dashboard.timeDatePicker.monthMar') || 'March',
    t('dashboard.timeDatePicker.monthApr') || 'April',
    t('dashboard.timeDatePicker.monthMay') || 'May',
    t('dashboard.timeDatePicker.monthJun') || 'June',
    t('dashboard.timeDatePicker.monthJul') || 'July',
    t('dashboard.timeDatePicker.monthAug') || 'August',
    t('dashboard.timeDatePicker.monthSep') || 'September',
    t('dashboard.timeDatePicker.monthOct') || 'October',
    t('dashboard.timeDatePicker.monthNov') || 'November',
    t('dashboard.timeDatePicker.monthDec') || 'December'
  ];

  const dayNames = [
    t('dashboard.timeDatePicker.daySun') || 'Sun',
    t('dashboard.timeDatePicker.dayMon') || 'Mon',
    t('dashboard.timeDatePicker.dayTue') || 'Tue',
    t('dashboard.timeDatePicker.dayWed') || 'Wed',
    t('dashboard.timeDatePicker.dayThu') || 'Thu',
    t('dashboard.timeDatePicker.dayFri') || 'Fri',
    t('dashboard.timeDatePicker.daySat') || 'Sat'
  ];

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDisplayTime = (timeStr) => {
    if (!timeStr) return '--';
    return timeStr;
  };

  const rangeStatusText = () => {
    if (!mode.includes('range')) return null;
    if (selectedDate && selectedEndDate) {
      return `${formatDisplayDate(selectedDate)} -> ${formatDisplayDate(selectedEndDate)}`;
    }
    if (selectedDate && !selectedEndDate) {
      return t('dashboard.timeDatePicker.selectEndDate') || 'Select an end date to finish the range';
    }
    return t('dashboard.timeDatePicker.selectDate') || 'Select a start date to begin';
  };


  const renderCalendar = () => (
    <div className="time-date-picker-calendar">
      <div className="time-date-picker-calendar-header">
        <button
          type="button"
          className="time-date-picker-nav-button"
          onClick={() => navigateMonth(-1)}
          aria-label={t('dashboard.timeDatePicker.previousMonth') || 'Previous month'}
        >
          ‹
        </button>
        <div className="time-date-picker-month-year">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
        <button
          type="button"
          className="time-date-picker-nav-button"
          onClick={() => navigateMonth(1)}
          aria-label={t('dashboard.timeDatePicker.nextMonth') || 'Next month'}
        >
          ›
        </button>
      </div>
      <div className="time-date-picker-calendar-grid">
        {dayNames.map((day) => (
          <div key={day} className="time-date-picker-day-header">
            {day}
          </div>
        ))}
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="time-date-picker-day empty" />;
          }
          const dateStr = formatDate(date);
          const isSelected = isDateSelected(date);
          const inRange = isDateInRange(date);
          const isRangeStart = mode.includes('range') && selectedDate && dateStr === selectedDate;
          const isRangeEnd = mode.includes('range') && selectedEndDate && dateStr === selectedEndDate;
          const isDisabled = isDateDisabled(date);
          const isToday = formatDate(new Date()) === dateStr;

          return (
            <button
              key={dateStr}
              type="button"
              className={`time-date-picker-day ${isSelected ? 'selected' : ''} ${inRange ? 'in-range' : ''} ${isRangeStart ? 'range-start' : ''} ${isRangeEnd ? 'range-end' : ''} ${isDisabled ? 'disabled' : ''} ${isToday ? 'today' : ''}`}
              onClick={(e) => handleDateClick(date, e)}
              disabled={isDisabled}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );


  const renderTimeSlider = (isEnd = false) => {
    const currentTime = isEnd ? selectedEndTime : selectedTime;
    const { hours, minutes } = parseTime(currentTime);

    return (
      <div className="time-date-picker-time-slider">
        <div className="time-date-picker-time-label">
          {isEnd
            ? (t('dashboard.endTime') || t('dashboard.timeDatePicker.endTime') || 'End Time')
            : (t('dashboard.startTime') || t('dashboard.timeDatePicker.startTime') || 'Start Time')}
        </div>
        <div className="time-date-picker-time-display">
          {currentTime}
        </div>
        <div className="time-date-picker-sliders-container">
          <div className="time-date-picker-slider-group">
            <label className="time-date-picker-slider-label">
              {t('dashboard.timeDatePicker.hours') || 'Hours'}
            </label>
            <input
              type="range"
              min="0"
              max="23"
              step="1"
              value={hours}
              onChange={(e) => handleHourChange(Number(e.target.value), isEnd)}
              className="time-date-picker-slider"
            />
            <div className="time-date-picker-slider-value">{hours}</div>
          </div>
          <div className="time-date-picker-slider-group">
            <label className="time-date-picker-slider-label">
              {t('dashboard.timeDatePicker.minutes') || 'Minutes'}
            </label>
            <input
              type="range"
              min="0"
              max="59"
              step="1"
              value={minutes}
              onChange={(e) => handleMinuteChange(Number(e.target.value), isEnd)}
              className="time-date-picker-slider"
            />
            <div className="time-date-picker-slider-value">{String(minutes).padStart(2, '0')}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderTimePicker = () => (
    <div className="time-date-picker-time-container">
      {renderTimeSlider(false)}
      {mode.includes('range') && mode.includes('time') && (
        <>
          <div style={{ height: '1rem' }} />
          {renderTimeSlider(true)}
        </>
      )}
    </div>
  );

  return (
    <div className="time-date-picker-modal-overlay">
      <div
        ref={modalRef}
        className={`time-date-picker-modal ${isRTL ? 'rtl' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="time-date-picker-modal-header">
          <h3>{title || (t('dashboard.timeDatePicker.selectDateTime') || 'Select Date & Time')}</h3>
          <button
            type="button"
            className="time-date-picker-close-button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
          >
            ×
          </button>
        </div>

        <div className="time-date-picker-modal-content">
          <div className="time-date-picker-selection-compact">
            <div className={`selection-pill ${!selectedDate ? 'pending' : ''}`}>
              <div className="pill-label">{t('jobs.startDate') || 'Start Date'}</div>
              <div className="pill-value">
                {formatDisplayDate(selectedDate)}
                {mode.includes('time') && (
                  <span className="pill-time">{formatDisplayTime(selectedTime)}</span>
                )}
              </div>
            </div>

            {mode.includes('range') && (
              <>
                <div className="selection-separator">→</div>
                <div className={`selection-pill ${!selectedEndDate ? 'pending' : ''}`}>
                  <div className="pill-label">{t('businessDashboard.endDate') || 'End Date'}</div>
                  <div className="pill-value">
                    {formatDisplayDate(selectedEndDate)}
                    {mode.includes('time') && (
                      <span className="pill-time">{formatDisplayTime(selectedEndTime)}</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {mode.includes('range') && (
            <div className="time-date-picker-selection-status compact">
              {rangeStatusText()}
            </div>
          )}

          {(mode.includes('date') || mode === 'datetime' || mode === 'datetimerange') && (
            <div className="time-date-picker-tabs">
              <button
                type="button"
                className={`time-date-picker-tab ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => setViewMode('calendar')}
              >
                {t('dashboard.timeDatePicker.calendar') || 'Calendar'}
              </button>
              {mode.includes('time') && (
                <button
                  type="button"
                  className={`time-date-picker-tab ${viewMode === 'time' ? 'active' : ''}`}
                  onClick={() => setViewMode('time')}
                >
                  {t('dashboard.timeDatePicker.time') || 'Time'}
                </button>
              )}
            </div>
          )}

          {viewMode === 'calendar' && (mode.includes('date') || mode === 'datetime' || mode === 'datetimerange') && renderCalendar()}
          {(viewMode === 'time' || mode === 'time' || mode === 'timerange') && renderTimePicker()}

          {mode === 'datetime' && viewMode === 'calendar' && (
            <div style={{ marginTop: '1rem' }}>
              {renderTimeSlider(false)}
            </div>
          )}

          {mode === 'datetimerange' && viewMode === 'calendar' && (
            <div style={{ marginTop: '1rem' }}>
              {renderTimeSlider(false)}
              <div style={{ height: '1rem' }} />
              {renderTimeSlider(true)}
            </div>
          )}
        </div>

        <div className="time-date-picker-modal-footer">
          <button
            type="button"
            className="time-date-picker-button cancel"
            onClick={onClose}
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            className="time-date-picker-button confirm"
            onClick={handleConfirm}
          >
            {t('common.confirm') || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeDatePickerModal;

